import { Component, computed, effect, inject, OnInit, OnDestroy, untracked } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, RouterOutlet } from '@angular/router';
import { environment } from '@envs/environment';
import { EventType } from 'app/shared/models/websocket/event.type';
import { WsMessageResponse } from 'app/shared/models/websocket/ws-message-response';
import { CognitoService } from 'app/shared/services/cognito.service';
import { ModalService } from 'app/shared/services/modal.service';
import { ReleaseNotesDialogService } from 'app/shared/services/release-notes-dialog.service';
import { ReleaseNotesService } from 'app/shared/services/release-notes.service';
import { WebSocketService } from 'app/shared/services/web-socket.service';
import { GlobalStore } from 'app/store/global.store';
import { UserPermissionsStore } from 'app/store/user-permissions.store';
import { usePreset } from '@primeng/themes';
import { IndagoNewPreset } from '../assets/presets/indagoai/indago-new.preset';
import { DialogService } from 'primeng/dynamicdialog';
import { Toast } from 'primeng/toast';
import { Subscription } from 'rxjs';
import { NavigationErrorToastComponent } from './shared/components/navigation-error-toast/navigation-error-toast.component';
import { PermissionsRetryBannerComponent } from './shared/components/permissions-retry-banner/permissions-retry-banner.component';
import { AppLoadingScreenComponent } from './shared/components/app-loading-screen/app-loading-screen.component';
import { closeOpenDialogs } from './shared/utils/close-open-dialogs.util';
import { permissionKeysForUrl } from './shared/utils/permission-route.util';

@Component({
  selector: 'inpla-root',
  imports: [RouterOutlet, Toast, NavigationErrorToastComponent, PermissionsRetryBannerComponent, AppLoadingScreenComponent],
  providers: [ReleaseNotesDialogService, DialogService, ModalService],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly titleService: Title = inject(Title);
  private readonly globalStore = inject(GlobalStore);
  private readonly cognitoService: CognitoService = inject(CognitoService);
  private readonly releaseNotesDialogService: ReleaseNotesDialogService = inject(ReleaseNotesDialogService);
  private readonly releaseNotesService: ReleaseNotesService = inject(ReleaseNotesService);
  private readonly websocketService: WebSocketService = inject(WebSocketService);
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);

  private permissionsWsSub: Subscription | undefined;
  private bootstrappedForUserId: string | null = null;

  public readonly userPermissionsStore = inject(UserPermissionsStore);
  public readonly isAwaitingPermissions = computed(
    () => !!this.globalStore.currentSession()?.idToken && !this.userPermissionsStore.settled()
  );

  constructor() {
    this.onReleaseNotesLoad();
    this.onSessionChange();
    this.redirectWhenCurrentModuleIsDenied();
  }

  public ngOnInit(): void {
    this.titleService.setTitle(environment.title);
    this.applyNewDesign();
    // Only publishes the session. Everything that depends on one is driven by the effect below,
    // because this method runs exactly once: AppComponent is the bootstrapped root and is never
    // re-created, while a sign-in that happens after bootstrap has to reach the same work.
    this.cognitoService
      .getCurrentSession()
      .then((it) => this.globalStore.setCurrentSession(it))
      .catch(() => {
        // Session retrieval failed — guard will timeout gracefully
      });
  }

  public ngOnDestroy() {
    this.permissionsWsSub?.unsubscribe();
    this.releaseNotesDialogService.destroyDialog();
  }

  public onPermissionsRetry(): void {
    this.userPermissionsStore.load();
  }

  /**
   * Per-session bootstrap. Signing in from `/auth` does not reload the page, so the app is
   * routinely bootstrapped with no session and the user acquires one later; hanging this work off
   * `ngOnInit` left it undone for the rest of the page's life.
   *
   * Keyed on `userId` so it runs once per signed-in user rather than on every session refresh,
   * and re-runs when a different user signs in without a reload.
   */
  private onSessionChange(): void {
    effect(() => {
      const session = this.globalStore.currentSession();
      // A signed-out `fetchAuthSession()` RESOLVES with an all-undefined session rather than
      // rejecting, so presence of the object proves nothing — the token is the real signal.
      const userId = session?.idToken ? session.userId : null;

      if (!userId) {
        // Sign-out, or a bootstrap with no session. Drop the keys so the next user is never
        // evaluated against the previous one's permissions.
        this.bootstrappedForUserId = null;
        this.userPermissionsStore.reset();
        return;
      }

      if (this.bootstrappedForUserId === userId) {
        return;
      }
      if (this.bootstrappedForUserId !== null) {
        this.userPermissionsStore.reset();
      }
      this.bootstrappedForUserId = userId;

      this.userPermissionsStore.load();
      this.initPermissionsWebSocketListener();
      this.globalStore.getReleaseNoteInfo();
      this.globalStore.loadLanguagePreference();
    });
  }

  private applyNewDesign(): void {
    document.documentElement.classList.add('new-design');
    usePreset(IndagoNewPreset as Record<string, unknown>);
  }

  private initPermissionsWebSocketListener(): void {
    // Idempotent: the effect above re-runs per user, but one subscription serves the page.
    if (this.permissionsWsSub) {
      return;
    }
    this.permissionsWsSub = this.websocketService.message.subscribe((res: WsMessageResponse) => {
      if (res.eventType === EventType.PERMISSIONS_CHANGED) {
        this.userPermissionsStore.load();
      }
    });
  }

  private redirectWhenCurrentModuleIsDenied(): void {
    effect(() => {
      if (this.userPermissionsStore.status() !== 'loaded') {
        return;
      }
      const hasAccess = this.userPermissionsStore.hasAccess();
      untracked(() => {
        if (permissionKeysForUrl(this.router.url).some((key) => !hasAccess(key))) {
          closeOpenDialogs(this.dialogService);
          void this.router.navigateByUrl('/not-found');
        }
      });
    });
  }

  private onReleaseNotesLoad(): void {
    effect(() => {
      if (this.globalStore.releaseNoteInfo().shouldShow) {
        // Re-runs when the session is nulled on sign-out, because getUserId() is a dependency.
        const userId = this.globalStore.getUserId();
        if (!userId) {
          return;
        }
        this.releaseNotesService.publishNotification(userId).subscribe({ error: () => {} });
      }
    });
  }
}
